import type { ZiweiChart, Palace, Star } from '@/lib/ziwei/types'
import { BRANCHES, STEMS } from '@/lib/ziwei/constants'

/** 地支 → CSS grid 位置（传统方盘：巳午未申 / 辰·酉 / 卯·戌 / 寅丑子亥） */
const BRANCH_GRID: Record<number, { col: number; row: number }> = {
  5: { col: 1, row: 1 },
  6: { col: 2, row: 1 },
  7: { col: 3, row: 1 },
  8: { col: 4, row: 1 },
  4: { col: 1, row: 2 },
  9: { col: 4, row: 2 },
  3: { col: 1, row: 3 },
  10: { col: 4, row: 3 },
  2: { col: 1, row: 4 },
  1: { col: 2, row: 4 },
  0: { col: 3, row: 4 },
  11: { col: 4, row: 4 },
}

function StarChip({ star }: { star: Star }) {
  return (
    <span className={`star ${star.type === 'major' ? 'major' : ''}`}>
      {star.name}
      {star.brightnessRaw ? <small style={{ opacity: 0.7 }}> {star.brightnessRaw}</small> : null}
      {star.siHua ? <span className={`sihua ${star.siHua}`}>{star.siHua}</span> : null}
    </span>
  )
}

function PalaceCell({ palace, onSelect }: { palace: Palace; onSelect?: (p: Palace) => void }) {
  const pos = BRANCH_GRID[palace.branch]
  const cls = [
    'palace',
    palace.isMingGong ? 'ming' : '',
    palace.isShenGong ? 'shen' : '',
  ]
    .filter(Boolean)
    .join(' ')
  return (
    <div
      className={cls}
      style={{ gridColumn: pos.col, gridRow: pos.row }}
      onClick={() => onSelect?.(palace)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') onSelect?.(palace)
      }}
    >
      <div className="head">
        <span className="name">{palace.name}</span>
        <span>
          {STEMS[palace.stem]}
          {BRANCHES[palace.branch]}
        </span>
      </div>
      <div className="stars">
        {palace.stars.slice(0, 10).map((s) => (
          <StarChip key={`${palace.branch}-${s.name}-${s.siHua ?? ''}`} star={s} />
        ))}
      </div>
      {palace.daXianAge ? (
        <div style={{ position: 'absolute', right: 6, bottom: 4, fontSize: 10, color: '#7a8499' }}>
          {palace.daXianAge[0]}-{palace.daXianAge[1]}
        </div>
      ) : null}
    </div>
  )
}

export default function ChartBoard({
  chart,
  onPalaceSelect,
}: {
  chart: ZiweiChart
  onPalaceSelect?: (p: Palace) => void
}) {
  const { birthInfo, lunarInfo, wuxingJuName, mingZhu, shenZhu, yinYangGender } = chart
  const ganZhiYear =
    lunarInfo.yearStem != null && lunarInfo.yearBranch != null
      ? `${STEMS[lunarInfo.yearStem]}${BRANCHES[lunarInfo.yearBranch]}`
      : ''

  return (
    <div className="chart-grid">
      {chart.palaces.map((p) => (
        <PalaceCell key={p.branch} palace={p} onSelect={onPalaceSelect} />
      ))}
      <div className="center-cell">
        <h2>紫微命盘</h2>
        <div className="sub">
          {birthInfo.year}-{String(birthInfo.month).padStart(2, '0')}-{String(birthInfo.day).padStart(2, '0')} ·{' '}
          {BRANCHES[birthInfo.hour]}时 · {birthInfo.gender === 'male' ? '男' : '女'}
        </div>
        <div className="sub">
          农历 {lunarInfo.lunarYear}年{lunarInfo.isLeapMonth ? '闰' : ''}
          {Math.abs(lunarInfo.lunarMonth)}月{lunarInfo.lunarDay}日
          {ganZhiYear ? ` · ${ganZhiYear}年` : ''}
        </div>
        <div className="sub">
          {wuxingJuName}
          {mingZhu ? ` · 命主${mingZhu}` : ''}
          {shenZhu ? ` · 身主${shenZhu}` : ''}
          {yinYangGender ? ` · ${yinYangGender}` : ''}
        </div>
        <div className="sub">红框=命宫 · 青边=身宫</div>
      </div>
    </div>
  )
}
